/**
 * One definition of "too small for AutoInvent".
 *
 * The same test was written out three times — in MobileBlocker, in an unused helper in
 * AuthContext, and it was about to be written a fourth time to record the signup device.
 * Three copies of a threshold is three chances for the wall to fire on a device the records
 * say was a desktop, which would make the signup field worse than useless: it would look
 * authoritative and disagree with what the visitor actually saw.
 *
 * So the blocker and the signup record now read from here, and a change to the threshold
 * moves both together.
 */

const MOBILE_UA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

/** Does the user agent name a handheld? */
export function hasMobileUserAgent() {
  try {
    return MOBILE_UA.test(navigator.userAgent);
  } catch {
    return false;
  }
}

/**
 * 600px, not 768px: an iPad in portrait reports exactly 768 and a desktop browser at
 * 150–200% zoom reports ~640–850, so the old threshold walled off tablets and anyone zoomed
 * in for readability. Phones (<= ~430px) are still caught here, and by the user-agent test
 * when the viewport lies.
 */
export const MOBILE_MAX_WIDTH = 600;

export function hasMobileViewport() {
  try {
    return window.innerWidth <= MOBILE_MAX_WIDTH;
  } catch {
    return false;
  }
}

/** True when MobileBlocker would show the desktop-required screen right now. */
export function isMobileDevice() {
  return hasMobileUserAgent() || hasMobileViewport();
}

/**
 * A snapshot for the user document, written once at signup beside the locale.
 *
 * Stores the raw signals as well as the verdict, for the same reason signupLocale.js stores
 * a timezone rather than a country: the threshold is a product decision that will move, and
 * a stored verdict computed under an old rule should still be re-derivable under a new one.
 */
export function captureSignupDevice() {
  const device = {
    capturedAt: new Date().toISOString(),
    isMobile: isMobileDevice(),
    matchedUserAgent: hasMobileUserAgent(),
  };

  try {
    device.viewportWidth = window.innerWidth;
  } catch {
    // Not worth failing a signup over.
  }

  return device;
}

/**
 * Browsers embedded inside another app, which is where a link tapped in a social feed opens.
 *
 * These matter for one reason: **they cannot complete an OAuth popup.** TikTok's WebView does
 * not merely block `window.open` — it intercepts the navigation and paints its own "Open this
 * link in your browser" screen over the app. So `window.open` never returns null, Firebase
 * believes the popup opened and waits for a result that cannot arrive, and the popup-blocked
 * error that would have triggered a fallback is never thrown. The person is stranded on
 * TikTok's screen with a raw auth URL on it.
 *
 * That is why this is a named list of apps rather than a feature test: there is no feature to
 * test. The failure is silent by construction, and the only signal available before it happens
 * is who is hosting the browser.
 *
 * TikTok is `BytedanceWebview` on both platforms, with `musical_ly` on iOS. The rest are here
 * because a creator's audience shares links onward and they all behave the same way.
 */
const IN_APP_BROWSERS = [
  ['tiktok', /BytedanceWebview|musical_ly/i],
  ['instagram', /Instagram/i],
  ['facebook', /FBAN|FBAV|FB_IAB/i],
  ['snapchat', /Snapchat/i],
  ['linkedin', /LinkedInApp/i],
  ['twitter', /Twitter for i(?:Phone|Pad)|TwitterAndroid/i],
  ['pinterest', /Pinterest\//i],
];

/**
 * The generic case: an embedded web view that never says which app is hosting it.
 *
 * TikTok is exactly this. Measured on an iPhone, its browser sends:
 *
 *   Mozilla/5.0 (iPhone; CPU iPhone OS 18_7 like Mac OS X) AppleWebKit/605.1.15
 *   (KHTML, like Gecko) Mobile/15E148
 *
 * No app name anywhere -- which is why the name list above found nothing and sign-in was
 * attempted twice into a dead end. What gives it away is the end of the string. Real iOS
 * Safari finishes `Version/18.7 Mobile/15E148 Safari/604.1`; a WKWebView drops both the
 * `Version/` and `Safari/` tokens. Every real iOS browser keeps `Safari/` and adds its own
 * marker (CriOS, FxiOS, EdgiOS, OPiOS), because they are all WebKit underneath.
 *
 * Android names its own: the `; wv)` in the platform section is the documented WebView flag.
 *
 * This is an absence rather than a signature, so it is the last test rather than the first --
 * a named app gives a better sentence to show somebody than "this app" does.
 */
function isEmbeddedWebView(ua) {
  if (/iPhone|iPad|iPod/i.test(ua)) {
    if (/CriOS|FxiOS|EdgiOS|OPiOS|Coast|Puffin/i.test(ua)) return false;
    return !/Safari\//.test(ua);
  }
  if (/Android/i.test(ua)) return /;\s*wv\)/i.test(ua);
  return false;
}

/**
 * Which app is hosting this browser, or null for a real one.
 *
 * Returns a name rather than a boolean so a failure can be reported per-app, and so the way
 * out can say "TikTok's built-in browser" when it knows and "this app" when it does not.
 */
export function inAppBrowserName() {
  try {
    const ua = navigator.userAgent;
    for (const [name, re] of IN_APP_BROWSERS) {
      if (re.test(ua)) return name;
    }
    return isEmbeddedWebView(ua) ? 'webview' : null;
  } catch {
    return null;
  }
}

/** True when an OAuth popup cannot work here and must not be attempted. */
export function isInAppBrowser() {
  return inAppBrowserName() !== null;
}

/** iOS, including iPadOS pretending to be a Mac. */
export function isIOS() {
  try {
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/i.test(ua)) return true;
    // iPadOS 13+ reports a desktop Safari UA; the touch points give it away.
    return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  } catch {
    return false;
  }
}

export function isAndroid() {
  try {
    return /Android/i.test(navigator.userAgent);
  } catch {
    return false;
  }
}
